import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import {
    getReviewAndPayCartState,
    isCartLoadedWithoutLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/LoadDataIfNeeded";
import setCartId from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetCartId";
import setInitialValues from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetInitialValues";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import cenSpcDeliveryFieldCompleted from "@tCentro/Handlers/CenSpcDeliveryFieldCompletedHandler";
import cenSpcOrderDetailsFieldCompleted from "@tCentro/Handlers/CenSpcOrderDetailsFieldCompletedHandler";
import cenSpcShippingFieldCompleted from "@tCentro/Handlers/CenSpcShippingFieldCompletedHandler";
import cenSpcIsShippingDisabled from "@tCentro/Handlers/CenSpcShippingDisabledHandler";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import React, { createContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const promotionsDataView = getReviewAndPayPromotions(state);
    const expand = state.data.carts.cartApiParameter?.expand || [];

    const isLoadDataNeeded =
        ((isCartLoadedWithoutLines(cartState) ||
            !cartState.value?.carriers ||
            !expand.includes("shipping") ||
            !expand.includes("tax")) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading);

    return {
        cartId,
        cartState,
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        isUpdatingCustomer: state.t_components.onePageCheckoutOnetimeAddress.isUpdatingCustomer,
        shouldLoadAccount: !getCurrentAccountState(state).value,
    };
};

const mapDispatchToProps = {
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    setPaymentDetailsState,
    cenSpcIsShippingDisabled,
    cenSpcShippingFieldCompleted,
    cenSpcOrderDetailsFieldCompleted,
    cenSpcDeliveryFieldCompleted,
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const OnePageCheckoutFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
});

const OnePageCheckoutPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    isUpdatingCustomer,
    shouldLoadAccount,
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    setPaymentDetailsState,
    cenSpcIsShippingDisabled,
    cenSpcShippingFieldCompleted,
    cenSpcOrderDetailsFieldCompleted,
    cenSpcDeliveryFieldCompleted,
}: Props) => {
    const [state] = useState<State>({
        formSubmitAttempted: false,
        validators: {},
    });

    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, onSuccess: () => setInitialValues() });
        }
    });

    useEffect(() => {
        setCartId({ cartId });
        // Always reset payment section when entering checkout so revisit from cart starts at billing/shipping flow.
        setPaymentDetailsState({ paymentDetailsState: "waiting" });
        // Reset custom section flow state so Shipping/Order/Delivery/Payment cannot stay completed after cart navigation.
        cenSpcIsShippingDisabled({ isShippingStateDisabledValue: true });
        cenSpcShippingFieldCompleted({ isShippingFieldCompletedValue: false });
        cenSpcOrderDetailsFieldCompleted({ isOrderDetailsFieldCompletedValue: false });
        cenSpcDeliveryFieldCompleted({ isDeliveryFieldCompletedValue: false });
        if (!isLoadDataNeeded) {
            setInitialValues();
        }
        if (shouldLoadAccount) {
            loadCurrentAccount();
        }
    }, []);

    // Prevent scroll restoration and force reset to top on mount and render
    React.useLayoutEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        // Disable browser scroll restoration entirely while on checkout
        const previousScrollRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = "manual";

        // Force scroll to top immediately before any rendering
        window.scrollTo(0, 0);

        // Prevent any scroll events during initial load
        const preventScroll = (e: Event) => {
            e.preventDefault();
        };
        window.addEventListener("scroll", preventScroll, { passive: false });

        // Queue scroll resets at multiple points to ensure it takes
        const scrollToTop = () => window.scrollTo(0, 0);
        scrollToTop();

        const raf1 = window.requestAnimationFrame(scrollToTop);
        const raf2 = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(scrollToTop);
        });
        const timeout = window.setTimeout(scrollToTop, 0);

        return () => {
            window.cancelAnimationFrame(raf1);
            window.cancelAnimationFrame(raf2);
            window.clearTimeout(timeout);
            window.removeEventListener("scroll", preventScroll);
            window.history.scrollRestoration = previousScrollRestoration;
        };
    }, []);

    return (
        <Page
            css={css`
                h1[data-test-selector^="PageTitle"] {
                    font-family: Poppins;
                    font-size: 56px;
                    font-weight: 800;
                    line-height: 64px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
            `}
        >
            <LoadingOverlay loading={isUpdatingCustomer}>
                <OnePageCheckoutFormContext.Provider value={state}>
                    <Zone zoneName="Content" contentId={id} />
                </OnePageCheckoutFormContext.Provider>
            </LoadingOverlay>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;
